import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ThreatDetectionPanel = ({ threats, loading, onRefresh, filters, onFilterChange }) => {
  const [selectedThreat, setSelectedThreat] = useState(null);

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'high':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const handleResponseAction = (threatId, action) => {
    console.log(`Executing ${action} for threat ${threatId}`);
    // In production, this would trigger automated response protocols
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          {[1, 2, 3]?.map(i => (
            <div key={i} className="h-24 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 flex items-center gap-2 flex-wrap">
          <Icon name="AlertTriangle" className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 flex-shrink-0" />
          <span className="break-words">Real-Time Threat Detection</span>
        </h2>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <select
            value={filters?.severity}
            onChange={(e) => onFilterChange?.({ ...filters, severity: e?.target?.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm flex-1 sm:flex-initial min-w-[140px]"
          >
            <option value="all">All Severities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          <Button onClick={onRefresh} variant="outline" size="sm" className="flex-shrink-0">
            <Icon name="RefreshCw" className="w-4 h-4" />
          </Button>
        </div>
      </div>
      {threats?.length === 0 ? (
        <div className="text-center py-12">
          <Icon name="Shield" className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No Active Threats Detected</h3>
          <p className="text-sm sm:text-base text-gray-600 max-w-md mx-auto">Your system is secure. All security protocols are functioning normally.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {threats?.map(threat => (
            <div
              key={threat?.id}
              className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
                    <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium border ${getSeverityColor(threat?.severity)} whitespace-nowrap`}>
                      {threat?.severity?.toUpperCase()}
                    </span>
                    <span className="text-xs sm:text-sm text-gray-500 break-words">
                      {new Date(threat?.createdAt)?.toLocaleString()}
                    </span>
                  </div>
                  <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2 break-words">{threat?.alertType}</h3>
                  <p className="text-sm sm:text-base text-gray-600 mb-3 break-words leading-relaxed">{threat?.message}</p>
                  <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-xs sm:text-sm text-gray-500">
                    <span className="flex items-center gap-1 break-words">
                      <Icon name="User" className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate max-w-[150px] sm:max-w-none">{threat?.affectedUser || 'System'}</span>
                    </span>
                    <span className="flex items-center gap-1 break-words">
                      <Icon name="MapPin" className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate max-w-[150px] sm:max-w-none">{threat?.sourceIp || 'N/A'}</span>
                    </span>
                  </div>
                </div>
                <div className="flex flex-row lg:flex-col gap-2 flex-shrink-0">
                  <Button
                    onClick={() => handleResponseAction(threat?.id, 'block')}
                    variant="outline"
                    size="sm"
                    className="text-red-600 border-red-300 hover:bg-red-50"
                  >
                    <Icon name="Ban" className="w-4 h-4" />
                    Block
                  </Button>
                  <Button
                    onClick={() => handleResponseAction(threat?.id, 'investigate')}
                    variant="outline"
                    size="sm"
                  >
                    <Icon name="Search" className="w-4 h-4" />
                    Investigate
                  </Button>
                  <Button
                    onClick={() => handleResponseAction(threat?.id, 'dismiss')}
                    variant="outline"
                    size="sm"
                  >
                    <Icon name="X" className="w-4 h-4" />
                    Dismiss
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ThreatDetectionPanel;